import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiPost } from '../utils/apiClient';
import type { CriticalityBand } from '../types/escalationMatrix.types';
import type { EscalationContactRole } from '../types/escalationContact.types';
import type {
  EscalationDashboardSummary,
  EscalationEvent,
  EscalationInstance,
  EscalationInstanceStatus,
  EscalationListParams,
  EscalationSlaStatus,
} from '../types/escalationRuntime.types';

function firstNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (value === undefined || value === null) {
      continue;
    }
    return Number(value);
  }
  return null;
}

function toDisplayString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

interface ApiEscalationResponse {
  success?: boolean;
  message?: string;
  count?: number;
  data?: Record<string, unknown> | Record<string, unknown>[];
}

function buildSearchParams(params: Record<string, string | number | boolean | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    search.set(key, String(value));
  });
  return search;
}

function mapEvent(raw: Record<string, unknown>): EscalationEvent {
  return {
    id: Number(raw.id),
    instanceId: Number(raw.instanceId ?? raw.instance_id),
    eventType: raw.eventType ?? raw.event_type,
    levelNumber: firstNumber(raw.levelNumber, raw.level_number),
    contactId: firstNumber(raw.contactId, raw.contact_id),
    role: (raw.role as string) ?? null,
    message: (raw.message as string) ?? null,
    metadata: (raw.metadata as Record<string, unknown>) ?? null,
    createdAt: toDisplayString(raw.createdAt ?? raw.created_at),
  };
}

function mapContact(raw: Record<string, unknown> | null | undefined) {
  if (!raw) {
    return null;
  }
  return {
    id: Number(raw.id),
    facilityId: Number(raw.facilityId ?? raw.facility_id),
    name: String(raw.name ?? ''),
    role: raw.role as EscalationContactRole,
    department: (raw.department as string) ?? null,
    mobileNumber: String(raw.mobileNumber ?? raw.mobile_number ?? ''),
    alternateMobileNumber: (raw.alternateMobileNumber ?? raw.alternate_mobile_number) as
      | string
      | null,
    status: (raw.status as 'Active' | 'Inactive') ?? 'Active',
  };
}

function mapCurrentLevel(raw: Record<string, unknown> | null | undefined) {
  if (!raw) {
    return null;
  }
  return {
    levelNumber: Number(raw.levelNumber ?? raw.level_number ?? 1),
    role: String(raw.role ?? ''),
    timerMinutes: Number(raw.timerMinutes ?? raw.timer_minutes ?? 0),
    orderIndex: Number(raw.orderIndex ?? raw.order_index ?? 0),
    contactId: Number(raw.contactId ?? raw.contact_id ?? 0),
  };
}

function mapInstance(raw: Record<string, unknown>): EscalationInstance {
  const study = raw.study as Record<string, unknown> | null | undefined;
  const matrix = raw.matrix as Record<string, unknown> | null | undefined;
  const events = Array.isArray(raw.events)
    ? raw.events.map(event => mapEvent(event as Record<string, unknown>))
    : [];

  return {
    id: Number(raw.id),
    facilityId: Number(raw.facilityId ?? raw.facility_id),
    studyId: Number(raw.studyId ?? raw.study_id),
    matrixId: Number(raw.matrixId ?? raw.matrix_id),
    criticalityBand: (raw.criticalityBand ?? raw.criticality_band) as CriticalityBand,
    status: (raw.status as EscalationInstanceStatus) ?? 'Active',
    currentLevelNumber: Number(raw.currentLevelNumber ?? raw.current_level_number ?? 1),
    currentContactId:
      raw.currentContactId != null
        ? Number(raw.currentContactId)
        : raw.current_contact_id != null
          ? Number(raw.current_contact_id)
          : null,
    currentContact: mapContact(
      (raw.currentContact ?? raw.current_contact) as Record<string, unknown>
    ),
    currentLevel: mapCurrentLevel(
      (raw.currentLevel ?? raw.current_level) as Record<string, unknown>
    ),
    reminderCountAtLevel: Number(raw.reminderCountAtLevel ?? raw.reminder_count_at_level ?? 0),
    triggeredLevels: Array.isArray(raw.triggeredLevels ?? raw.triggered_levels)
      ? (raw.triggeredLevels ?? raw.triggered_levels).map(Number)
      : [],
    levelStartedAt: (raw.levelStartedAt ?? raw.level_started_at) as string | null | undefined,
    nextActionAt: (raw.nextActionAt ?? raw.next_action_at) as string | null | undefined,
    timeRemainingSeconds: Number(raw.timeRemainingSeconds ?? raw.time_remaining_seconds ?? 0),
    levelElapsedSeconds: Number(raw.levelElapsedSeconds ?? raw.level_elapsed_seconds ?? 0),
    isReminderPhase: Boolean(raw.isReminderPhase ?? raw.is_reminder_phase),
    remindersRemaining: Number(raw.remindersRemaining ?? raw.reminders_remaining ?? 0),
    slaStatus: (raw.slaStatus ?? raw.sla_status ?? 'N/A') as EscalationSlaStatus,
    slaDeadlineAt: (raw.slaDeadlineAt ?? raw.sla_deadline_at) as string | null | undefined,
    slaProgressPercent: Number(raw.slaProgressPercent ?? raw.sla_progress_percent ?? 0),
    phaseDurationSeconds: Number(raw.phaseDurationSeconds ?? raw.phase_duration_seconds ?? 0),
    pendingAcknowledgement: Boolean(raw.pendingAcknowledgement ?? raw.pending_acknowledgement),
    acknowledgedAt: (raw.acknowledgedAt ?? raw.acknowledged_at) as string | null | undefined,
    acknowledgedBy:
      raw.acknowledgedBy != null
        ? Number(raw.acknowledgedBy)
        : raw.acknowledged_by != null
          ? Number(raw.acknowledged_by)
          : null,
    startedAt: String(raw.startedAt ?? raw.started_at ?? ''),
    completedAt: (raw.completedAt ?? raw.completed_at) as string | null | undefined,
    study: study
      ? {
          id: Number(study.id),
          studyInstanceUid: String(study.studyInstanceUid ?? study.study_instance_uid ?? ''),
          isCritical: Boolean(study.isCritical ?? study.is_critical),
          criticalReason: (study.criticalReason ?? study.critical_reason) as string | null,
          criticalityBand: (study.criticalityBand ??
            study.criticality_band) as CriticalityBand | null,
          accessionNumber: (study.accessionNumber ?? study.accession_number) as string | null,
          risVisitId: (study.risVisitId ?? study.ris_visit_id) as string | null,
          studyDescription: (study.studyDescription ?? study.study_description) as string | null,
          studyDisplayName: (study.studyDisplayName ?? study.study_display_name) as string | null,
          testName: (study.testName ?? study.test_name) as string | null,
          modalityName: (study.modalityName ?? study.modality_name) as string | null,
          patientName: (study.patientName ?? study.patient_name) as string | null,
          patientUhid: (study.patientUhid ?? study.patient_uhid) as string | null,
          referredBy: (study.referredBy ?? study.referred_by) as string | null,
        }
      : null,
    matrix: matrix
      ? {
          id: Number(matrix.id),
          reminderIntervalMinutes: Number(
            matrix.reminderIntervalMinutes ?? matrix.reminder_interval_minutes ?? 5
          ),
          maxReminderAttempts: Number(
            matrix.maxReminderAttempts ?? matrix.max_reminder_attempts ?? 3
          ),
        }
      : null,
    events,
    criticalNotification: (() => {
      const cn = (raw.criticalNotification ?? raw.critical_notification) as
        | Record<string, unknown>
        | null
        | undefined;
      if (!cn?.id) {
        return null;
      }
      return {
        id: Number(cn.id),
        notificationStatus: (cn.notificationStatus ?? cn.notification_status) as string | null,
        acknowledgementStatus: (cn.acknowledgementStatus ?? cn.acknowledgement_status) as
          | string
          | null,
        acknowledgedAt: (cn.acknowledgedAt ?? cn.acknowledged_at) as string | null,
        acknowledgementMethod: (cn.acknowledgementMethod ?? cn.acknowledgement_method) as
          | string
          | null,
      };
    })(),
    createdAt: raw.createdAt as string | undefined,
    updatedAt: raw.updatedAt as string | undefined,
  };
}

function mapSummary(raw: Record<string, unknown>): EscalationDashboardSummary {
  const byBand = (raw.byBand ?? raw.by_band ?? {}) as Record<string, number>;
  const statusCounts = (raw.statusCounts ?? raw.status_counts ?? {}) as Record<string, number>;
  return {
    active: Number(raw.active ?? 0),
    pendingAcknowledgement: Number(raw.pendingAcknowledgement ?? raw.pending_acknowledgement ?? 0),
    dueSoon: Number(raw.dueSoon ?? raw.due_soon ?? 0),
    overdue: Number(raw.overdue ?? 0),
    slaBreaches: Number(raw.slaBreaches ?? raw.sla_breaches ?? raw.overdue ?? 0),
    avgAckTatSeconds: Number(raw.avgAckTatSeconds ?? raw.avg_ack_tat_seconds ?? 0),
    activeContacts: Number(raw.activeContacts ?? raw.active_contacts ?? 0),
    statusCounts: {
      pending: Number(statusCounts.pending ?? 0),
      escalating: Number(statusCounts.escalating ?? 0),
      breached: Number(statusCounts.breached ?? 0),
      closed: Number(statusCounts.closed ?? 0),
    },
    byBand: {
      Major: Number(byBand.Major ?? 0),
      Minor: Number(byBand.Minor ?? 0),
    },
  };
}

export async function getEscalationSummary(params: {
  facilityId: number;
  criticalityBand?: CriticalityBand;
  startedAfter?: string;
  startedBefore?: string;
}) {
  try {
    const search = buildSearchParams(params);
    const response = await apiGet<ApiEscalationResponse>(
      `${API_ENDPOINTS.ESCALATION_INSTANCES.SUMMARY}?${search.toString()}`,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      return { success: false as const, error: body?.message || 'Failed to load summary' };
    }
    return {
      success: true as const,
      data: mapSummary(body.data as Record<string, unknown>),
    };
  } catch (err) {
    return {
      success: false as const,
      error: err instanceof Error ? err.message : 'Failed to load summary',
    };
  }
}

export async function listEscalationInstances(params: EscalationListParams) {
  try {
    const search = buildSearchParams({
      facilityId: params.facilityId,
      status: params.status,
      criticalityBand: params.criticalityBand,
      pendingOnly: params.pendingOnly,
      startedAfter: params.startedAfter,
      startedBefore: params.startedBefore,
      limit: params.limit,
      offset: params.offset,
    });

    const response = await apiGet<ApiEscalationResponse>(
      `${API_ENDPOINTS.ESCALATION_INSTANCES.LIST}?${search.toString()}`,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false) {
      return {
        success: false as const,
        data: [],
        count: 0,
        error: body.message || 'Failed to load escalations',
      };
    }
    const rows = (Array.isArray(body?.data) ? body.data : []).map(item =>
      mapInstance(item as Record<string, unknown>)
    );
    return { success: true as const, data: rows, count: body?.count ?? rows.length };
  } catch (err) {
    return {
      success: false as const,
      data: [],
      count: 0,
      error: err instanceof Error ? err.message : 'Failed to load escalations',
    };
  }
}

export async function listPendingEscalationInstances(params: EscalationListParams) {
  try {
    const search = buildSearchParams({
      facilityId: params.facilityId,
      criticalityBand: params.criticalityBand,
      startedAfter: params.startedAfter,
      startedBefore: params.startedBefore,
      limit: params.limit,
      offset: params.offset,
    });

    const response = await apiGet<ApiEscalationResponse>(
      `${API_ENDPOINTS.ESCALATION_INSTANCES.PENDING}?${search.toString()}`,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false) {
      return {
        success: false as const,
        data: [],
        count: 0,
        error: body.message || 'Failed to load pending escalations',
      };
    }
    const rows = (Array.isArray(body?.data) ? body.data : []).map(item =>
      mapInstance(item as Record<string, unknown>)
    );
    return { success: true as const, data: rows, count: body?.count ?? rows.length };
  } catch (err) {
    return {
      success: false as const,
      data: [],
      count: 0,
      error: err instanceof Error ? err.message : 'Failed to load pending escalations',
    };
  }
}

export async function getEscalationInstance(id: number, facilityId: number) {
  try {
    const response = await apiGet<ApiEscalationResponse>(
      `${API_ENDPOINTS.ESCALATION_INSTANCES.DETAIL(id)}?facilityId=${facilityId}`,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      return { success: false as const, error: body?.message || 'Escalation not found' };
    }
    return { success: true as const, data: mapInstance(body.data as Record<string, unknown>) };
  } catch (err) {
    return {
      success: false as const,
      error: err instanceof Error ? err.message : 'Failed to load escalation',
    };
  }
}

export async function getEscalationEvents(
  id: number,
  facilityId: number,
  params?: { limit?: number; offset?: number }
) {
  try {
    const search = buildSearchParams({
      facilityId,
      limit: params?.limit,
      offset: params?.offset,
    });
    const response = await apiGet<ApiEscalationResponse>(
      `${API_ENDPOINTS.ESCALATION_INSTANCES.EVENTS(id)}?${search.toString()}`,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false) {
      return {
        success: false as const,
        data: [],
        count: 0,
        error: body?.message || 'Failed to load history',
      };
    }
    const rows = (Array.isArray(body?.data) ? body.data : []).map(item =>
      mapEvent(item as Record<string, unknown>)
    );
    return { success: true as const, data: rows, count: body?.count ?? rows.length };
  } catch (err) {
    return {
      success: false as const,
      data: [],
      count: 0,
      error: err instanceof Error ? err.message : 'Failed to load history',
    };
  }
}

export async function acknowledgeEscalationInstance(id: number, facilityId: number) {
  try {
    const response = await apiPost<ApiEscalationResponse>(
      API_ENDPOINTS.ESCALATION_INSTANCES.ACKNOWLEDGE(id),
      { facilityId },
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      return {
        success: false as const,
        error: body?.message || 'Failed to acknowledge escalation',
      };
    }
    return { success: true as const, data: mapInstance(body.data as Record<string, unknown>) };
  } catch (err) {
    return {
      success: false as const,
      error: err instanceof Error ? err.message : 'Failed to acknowledge escalation',
    };
  }
}
